// services/ugc/momentApi.ts

import type { AxiosRequestConfig, AxiosProgressEvent } from "axios";
import baseApi from "../../lib/baseApi";
import uploadApi from "../../lib/uploadApi";

import type {
  MomentDTO,
  MomentFeedResponse,
  CreateMomentPayload,
  UpdateMomentPayload,
  CreateMomentImagePayload,
  CreateMomentLegacyImagePayload,
  CreateMomentVideoPayload,
} from "../../types/ugc/momentTypes";
import { UGC_ENDPOINTS } from "./endpoint/endpoints";

// ------------------------------------------------------------
// Constants
// ------------------------------------------------------------
const ROOT = UGC_ENDPOINTS.moments.root;

// ------------------------------------------------------------
// Upload progress helpers (shared pattern)
// ------------------------------------------------------------
type ProgressOpts = {
  onUploadProgress?: (pct: number) => void;
  signal?: AbortSignal;
};

const withProgress = (opts?: ProgressOpts): AxiosRequestConfig => ({
  signal: opts?.signal,
  onUploadProgress: (evt: AxiosProgressEvent) => {
    if (!opts?.onUploadProgress) return;

    const pct =
      typeof evt.progress === "number"
        ? Math.round(evt.progress * 100)
        : evt.total
        ? Math.round((evt.loaded / evt.total) * 100)
        : 0;

    opts.onUploadProgress(pct);
  },
});

// ------------------------------------------------------------
// Param helpers (keep API consistent, avoid falsy traps)
// ------------------------------------------------------------
type CursorOpts = { cursor?: string; pageSize?: number };
type PageOpts = { page?: number; pageSize?: number };

const withCursorParams = (opts?: CursorOpts) => {
  const params: Record<string, any> = {};
  if (opts?.cursor) params.cursor = opts.cursor;

  if (typeof opts?.pageSize === "number") params.page_size = opts.pageSize;

  return Object.keys(params).length ? { params } : undefined;
};

const withPageParams = (opts?: PageOpts) => {
  const params: Record<string, any> = {};

  if (typeof opts?.page === "number" && opts.page > 0) params.page = opts.page;

  if (typeof opts?.pageSize === "number") params.page_size = opts.pageSize;

  return Object.keys(params).length ? { params } : undefined;
};

// ------------------------------------------------------------
// FormData guards
// ------------------------------------------------------------
const isBrowserFormData = (value: unknown): value is FormData =>
  typeof FormData !== "undefined" && value instanceof FormData;

const isFile = (value: unknown): value is File =>
  typeof File !== "undefined" && value instanceof File;

const appendOptionalText = (
  formData: FormData,
  name: string,
  value?: string | null
) => {
  if (value === undefined || value === null) return;
  formData.append(name, value);
};

const appendOptionalFile = (
  formData: FormData,
  name: string,
  file?: File | null
) => {
  if (!file) return;
  formData.append(name, file);
};

const isCreateVideoPayload = (
  payload: CreateMomentPayload
): payload is CreateMomentVideoPayload => {
  return isFile((payload as CreateMomentVideoPayload).video);
};

const isCreateMultiImagePayload = (
  payload: CreateMomentPayload
): payload is CreateMomentImagePayload => {
  return Array.isArray((payload as CreateMomentImagePayload).images);
};

const isCreateLegacyImagePayload = (
  payload: CreateMomentPayload
): payload is CreateMomentLegacyImagePayload => {
  return isFile((payload as CreateMomentLegacyImagePayload).image);
};

const orderImagesForUpload = (
  images: File[],
  coverImageIndex?: number
): File[] => {
  if (!images.length) return [];

  if (
    typeof coverImageIndex !== "number" ||
    coverImageIndex < 0 ||
    coverImageIndex >= images.length
  ) {
    return images;
  }

  const cover = images[coverImageIndex];
  const rest = images.filter((_, index) => index !== coverImageIndex);

  // Backend uses the first uploaded image as initial cover.
  return [cover, ...rest];
};

const createPayloadToFormData = (
  payload: CreateMomentPayload
): FormData => {
  const formData = new FormData();

  appendOptionalText(formData, "caption", payload.caption ?? null);
  appendOptionalText(formData, "visibility", payload.visibility ?? null);

  if (isCreateVideoPayload(payload)) {
    formData.append("video", payload.video);
    appendOptionalFile(formData, "thumbnail", payload.thumbnail);
    return formData;
  }

  if (isCreateMultiImagePayload(payload)) {
    const images = orderImagesForUpload(
      payload.images,
      payload.coverImageIndex
    );

    images.forEach((file) => {
      formData.append("images", file);
    });

    return formData;
  }

  if (isCreateLegacyImagePayload(payload)) {
    // Backward compatibility with old create form.
    formData.append("image", payload.image);
    appendOptionalFile(formData, "thumbnail", payload.thumbnail);
    return formData;
  }

  return formData;
};

const updatePayloadToBody = (
  payload: UpdateMomentPayload
): FormData | Record<string, unknown> => {
  const hasThumbnail = isFile(payload.thumbnail);

  if (hasThumbnail) {
    const formData = new FormData();

    appendOptionalText(formData, "caption", payload.caption ?? null);
    appendOptionalText(formData, "visibility", payload.visibility ?? null);
    appendOptionalText(formData, "cover_image_id", payload.cover_image_id ?? null);
    appendOptionalFile(formData, "thumbnail", payload.thumbnail);

    return formData;
  }

  return {
    caption: payload.caption ?? "",
    ...(payload.visibility ? { visibility: payload.visibility } : {}),
    ...(payload.cover_image_id ? { cover_image_id: payload.cover_image_id } : {}),
  };
};

// ------------------------------------------------------------
// Moment API
// ------------------------------------------------------------
export const momentApi = {
  // ----------------------------------------------------------
  // Feed (home timeline – cursor based)
  // GET /posts/moments/feed/
  // ----------------------------------------------------------
  getFeed: async (cursor?: string) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/feed/`,
      withCursorParams({ cursor })
    );
    return data;
  },

  getFeedWith: async (opts?: CursorOpts) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/feed/`,
      withCursorParams(opts)
    );
    return data;
  },

  // ----------------------------------------------------------
  // Trending (global – cursor based)
  // GET /posts/moments/trending/
  // ----------------------------------------------------------
  getTrending: async (cursor?: string) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/trending/`,
      withCursorParams({ cursor })
    );
    return data;
  },

  getTrendingWith: async (opts?: CursorOpts) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/trending/`,
      withCursorParams(opts)
    );
    return data;
  },

  // ----------------------------------------------------------
  // Personalized trending (for me)
  // GET /posts/moments/trending_for_me/
  // ----------------------------------------------------------
  getTrendingForMe: async (cursor?: string) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/trending_for_me/`,
      withCursorParams({ cursor })
    );
    return data;
  },

  getTrendingForMeWith: async (opts?: CursorOpts) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/trending_for_me/`,
      withCursorParams(opts)
    );
    return data;
  },

  // ----------------------------------------------------------
  // Explore (public discover)
  // GET /posts/moments/explore/
  // ----------------------------------------------------------
  getExplore: async (page?: number) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/explore/`,
      withPageParams({ page })
    );
    return data;
  },

  getExploreWith: async (opts?: PageOpts) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/explore/`,
      withPageParams(opts)
    );
    return data;
  },

  // ----------------------------------------------------------
  // My Moments
  // GET /posts/moments/me/
  // ----------------------------------------------------------
  getMyMoments: async (page?: number) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/me/`,
      withPageParams({ page })
    );
    return data;
  },

  getMyMomentsWith: async (opts?: PageOpts) => {
    const { data } = await baseApi.get<MomentFeedResponse>(
      `${ROOT}/me/`,
      withPageParams(opts)
    );
    return data;
  },

  // ----------------------------------------------------------
  // Retrieve by slug
  // GET /posts/moments/{slug}/
  // ----------------------------------------------------------
  getBySlug: async (slug: string) => {
    const { data } = await baseApi.get<MomentDTO>(`${ROOT}/${slug}/`);
    return data;
  },

  // ----------------------------------------------------------
  // Create Moment
  // POST /posts/moments/
  // ----------------------------------------------------------
  create: async (
    payload: FormData | CreateMomentPayload,
    opts?: ProgressOpts
  ) => {
    const body = isBrowserFormData(payload)
      ? payload
      : createPayloadToFormData(payload);

    const { data } = await uploadApi.post<MomentDTO>(
      `${ROOT}/`,
      body,
      withProgress(opts)
    );

    return data;
  },

  // ----------------------------------------------------------
  // Update Moment
  // PATCH /posts/moments/{slug}/
  // ----------------------------------------------------------
  updateBySlug: async (
    slug: string,
    payload: FormData | UpdateMomentPayload,
    opts?: ProgressOpts
  ) => {
    const body = isBrowserFormData(payload)
      ? payload
      : updatePayloadToBody(payload);

    const client = isBrowserFormData(body) ? uploadApi : baseApi;

    const { data } = await client.patch<MomentDTO>(
      `${ROOT}/${slug}/`,
      body,
      withProgress(opts)
    );

    return data;
  },

  // ----------------------------------------------------------
  // Delete Moment
  // DELETE /posts/moments/{slug}/
  // ----------------------------------------------------------
  removeBySlug: async (slug: string) => {
    const { status } = await baseApi.delete(`${ROOT}/${slug}/`);
    return status === 204;
  },
};

export default momentApi;